import downloadjs from "downloadjs";
import Papa from "papaparse";
import Canvg from "canvg";
import ReactGA from "react-ga4";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { Mixpanel, MxE } from "./mixpanelWrapper";
import * as R from "ramda";
import { FilterPaneState } from "@blisspointmedia/bpm-types/dist/FilterPane";
import { FilterCategory } from "@blisspointmedia/bpm-types/dist/Performance";

export const download = (
  csv: string | File | Blob | Uint8Array,
  fileName: string,
  mimeType?: string | undefined
): void => {
  // Log to GA4 so we can keep track of what's being downloaded from the app.
  ReactGA.event({
    category: "download",
    action: "download",
    label: fileName,
  });
  Mixpanel.track(MxE.CSV_DOWNLOAD, {
    fileName: fileName,
  });

  downloadjs(csv, fileName, mimeType);
};

export const downloadJSONToCSV = (data: Record<string, any>[], fileName: string): void => {
  if (!(data && fileName)) {
    console.error("Must provide data and fileName to download JSON to CSV");
    return;
  }
  const csv = Papa.unparse(data);
  download(csv, `${fileName}.csv`, "text/csv");
};

export const convertSVGStringToPNGURI = (
  svgString: string,
  width = 1920,
  height = 1080
): string => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }
  let v = Canvg.fromString(ctx, svgString);
  v.resize(width, height, true);
  v.render();

  let pngUri = canvas.toDataURL("image/png");
  canvas.remove();

  return pngUri;
};

/*
 * Export an array of objects to an Excel file.
 */
export const exportToExcel = (data: Record<string, any>[], name: string): void => {
  const resolvedTitle = name.length > 31 ? name.slice(0, 31) : name;
  const fileName = `${name}.xlsx`;
  const sheetName = resolvedTitle
    .replace(/\[/g, "(")
    .replace(/\]/g, ")")
    .replace(/[:/]/g, "-")
    .replace(/[\\?*]/g, "_")
    .toLowerCase();

  let workbook: XLSX.WorkBook = { SheetNames: [], Sheets: {} };
  let worksheet = XLSX.utils.json_to_sheet(data);

  workbook.SheetNames.push(sheetName);
  workbook.Sheets[sheetName] = worksheet;

  Mixpanel.track(MxE.EXCEL_DOWNLOAD, {
    fileName: name,
  });

  XLSX.writeFile(workbook, fileName);
};

export interface Sheet {
  name: string;
  data: Record<string, any>[];
}
export const exportMultiSheetToExcel = (data: Sheet[], fileName: string): void => {
  let workbook: XLSX.WorkBook = { SheetNames: [], Sheets: {} };
  let sheetNameFreqMap = new Map<string, number>();

  for (const sheet of data) {
    const { name, data } = sheet;
    let resolvedTitle = name.length > 31 ? name.slice(0, 31) : name;
    resolvedTitle = resolvedTitle
      .replace(/\[/g, "(")
      .replace(/\]/g, ")")
      .replace(/[:/]/g, "-")
      .replace(/[\\?*]/g, "_")
      .toLowerCase();
    const worksheet = XLSX.utils.json_to_sheet(data);
    if (resolvedTitle in sheetNameFreqMap) {
      let i = sheetNameFreqMap[resolvedTitle] + 1;
      sheetNameFreqMap[resolvedTitle] = i;
      resolvedTitle.length + i.toString().length <= 31
        ? (resolvedTitle = resolvedTitle.concat(i.toString()))
        : (resolvedTitle = resolvedTitle.slice(0, 31 - i.toString().length).concat(i.toString()));
    } else {
      sheetNameFreqMap[resolvedTitle] = 1;
    }
    workbook.SheetNames.push(resolvedTitle);
    workbook.Sheets[resolvedTitle] = worksheet;
  }

  const resolvedFileName = fileName.length > 31 ? fileName.slice(0, 31) : fileName;

  XLSX.writeFile(workbook, `${resolvedFileName}.xlsx`);
};

/*
 * Download a PNG of an HTML element.
 */
export const downloadPNG = async (
  elementName: string,
  fileName: string | string[],
  label = "",
  classNamesToRemove: string[] = []
): Promise<void> => {
  const ourFileNameString = Array.isArray(fileName) ? fileName.join("_") : fileName;
  const ourFileName = `${ourFileNameString.replace(/[- ]/g, char =>
    char === "-" ? "." : ""
  )}.png`;
  const contents = document.querySelector<HTMLElement>(elementName);
  if (!contents) {
    return;
  }

  Mixpanel.track(MxE.PNG_DOWNLOAD, {
    fileName: ourFileName,
    label: `${label} ${elementName}`,
  });

  const originalStyles = {
    height: contents.style.height,
    width: contents.style.width,
    maxHeight: contents.style.maxHeight,
    maxWidth: contents.style.maxWidth,
    overflow: contents.style.overflow,
    scrollTop: contents.scrollTop,
    scrollLeft: contents.scrollLeft,
  };

  const fullHeight = contents.scrollHeight;
  const fullWidth = contents.scrollWidth;

  contents.style.height = `${fullHeight}px`;
  contents.style.width = `${fullWidth}px`;
  contents.style.maxHeight = "none";
  contents.style.maxWidth = "none";
  contents.style.overflow = "visible";
  contents.scrollTop = 0;
  contents.scrollLeft = 0;

  const options = {
    backgroundColor: null,
    onclone: (_, el) => {
      const chart = el.querySelector(".chartContainer");
      if (chart) {
        chart.style.backgroundColor = "white";
      } else {
        el.style.backgroundColor = "white";
      }
      for (const name of classNamesToRemove) {
        const elementToRemove = el.querySelector(name);
        if (elementToRemove) {
          elementToRemove.remove();
        }
      }
    },
    scale: 1,
  };
  try {
    const canvas = await html2canvas(contents, options);

    const newCanvas = document.createElement("canvas");
    newCanvas.width = canvas.width;
    newCanvas.height = canvas.height + 30;
    const newCtx = newCanvas.getContext("2d");

    if (newCtx && label.length) {
      newCtx.drawImage(canvas, 0, 0);
      newCtx.font = "16px Graphik";
      newCtx.fillStyle = "black";
      newCtx.textAlign = "start";
      newCtx.fillText(label, 15, canvas.height + 20);
      const dataURL = newCanvas.toDataURL("image/png");
      downloadjs(dataURL, ourFileName, "image/png");
    } else {
      const dataURL = canvas.toDataURL("image/png");
      downloadjs(dataURL, ourFileName, "image/png");
    }
  } finally {
    contents.style.height = originalStyles.height;
    contents.style.width = originalStyles.width;
    contents.style.maxHeight = originalStyles.maxHeight;
    contents.style.maxWidth = originalStyles.maxWidth;
    contents.style.overflow = originalStyles.overflow;
    contents.scrollTop = originalStyles.scrollTop;
    contents.scrollLeft = originalStyles.scrollLeft;
  }
};

interface Filters {
  [segment: string]: {
    values: string;
    isInclusion: boolean;
  };
}

export interface DataInfo {
  KPI: string;
  Source: string;
  filters: Filters;
}

interface DataInfoParams {
  kpi: string;
  sources: string;
  filterData: FilterCategory[];
  filterState: FilterPaneState;
}

/**
 * Get a summary of the KPI, source, and filters that are currently applied to a page.
 */
export const getDataInfo = ({
  kpi,
  sources,
  filterData,
  filterState,
}: DataInfoParams): DataInfo => {
  const selectedMap = R.pathOr({}, ["basic", "selectedMap"], filterState); // Map of categories to selected items
  const notMap = R.pathOr({}, ["basic", "notMap"], filterState); // The categories in selectedMap that are exclusions
  const filters: Filters = {};

  for (const item of filterData) {
    const { label, key } = item;

    const selectedValues = selectedMap[key] || {};
    let values: string[] = [];

    if (R.keys(selectedValues).length > 0) {
      values = R.keys(selectedValues);
    }

    filters[label] = {
      values: values.length === 0 ? "All" : values.join(", "),
      isInclusion: !notMap[key],
    };
  }

  return {
    KPI: kpi,
    Source: sources,
    filters,
  };
};

export const dataInfoExcelExport = (dataInfo: DataInfo): Record<string, string>[] => {
  let res: Record<string, string>[] = [];

  res.push({ "Data Info": `KPI = ${dataInfo.KPI}` });
  res.push({ "Data Info": `Source = ${dataInfo.Source}` });
  Object.entries(dataInfo.filters).forEach(([key, value]) => {
    const { values, isInclusion } = value;
    const text = `${key} = ${isInclusion ? "" : "Exlude"} ${values}`;
    res.push({ "Data Info": text });
  });

  return res;
};
